import { useState } from "react";
import { Loader2, LocateFixed, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { api } from "@/lib/api";
import { dateLocale } from "@/lib/dateTime";
import { getPosition, metres, useVisitSettings } from "@/lib/visits";

/**
 * Where a lead or account is, for measuring visit check-ins. A rep standing at the customer saves it with one tap;
 * until it is saved, check-ins there can't be checked for distance.
 */
export function CustomerLocation({
  type,
  id,
  lat,
  lng,
  setAt,
  canEdit,
}: {
  type: "LEAD" | "ACCOUNT";
  id: string;
  lat?: number | null;
  lng?: number | null;
  setAt?: string | null;
  canEdit: boolean;
}) {
  const { hasPermission } = useAuth();
  const settings = useVisitSettings(hasPermission("visit.read"));
  const { success, error } = useToast();
  const [saved, setSaved] = useState<{ lat: number; lng: number; at: string } | null>(lat != null && lng != null ? { lat: Number(lat), lng: Number(lng), at: setAt || "" } : null);
  const [busy, setBusy] = useState(false);
  if (!settings?.enabled) return null;

  const save = async () => {
    setBusy(true);
    try {
      const pos = await getPosition();
      if (pos.accuracy > 150 && !window.confirm(`Your location is only accurate to about ${metres(pos.accuracy)}. Save it anyway?`)) return;
      await api.post("/api/FieldVisit/CustomerLocation", { related_type: type, related_id: id, lat: pos.lat, lng: pos.lng, accuracy: pos.accuracy });
      setSaved({ lat: pos.lat, lng: pos.lng, at: new Date().toISOString() });
      success("Location saved", "Visit check-ins here are now measured from this spot.");
    } catch (e: any) {
      error("Could not save the location", e.message || "Please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="flex flex-col gap-1.5 rounded-lg border border-border bg-card px-3.5 py-3">
      <h2 className="text-[11.5px] font-semibold uppercase tracking-wide text-muted-foreground">Customer location</h2>
      {saved ? (
        <a href={`https://www.openstreetmap.org/?mlat=${saved.lat}&mlon=${saved.lng}#map=18/${saved.lat}/${saved.lng}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-[13px] font-medium text-primary hover:underline">
          <MapPin className="size-3.5" /> {saved.lat.toFixed(5)}, {saved.lng.toFixed(5)}
        </a>
      ) : (
        <p className="text-[13px] text-muted-foreground">Not saved yet. Save it while you're at the customer, so visit check-ins can be checked.</p>
      )}
      {saved?.at && <span className="text-xs text-muted-foreground">Saved {new Date(saved.at).toLocaleDateString(dateLocale(), { day: "numeric", month: "short", year: "numeric" })}</span>}
      {canEdit && (
        <Button variant="outline" size="sm" className="mt-1 self-start" disabled={busy} onClick={save}>
          {busy ? <Loader2 className="mr-1.5 size-3.5 animate-spin" /> : <LocateFixed className="mr-1.5 size-3.5" />}
          {saved ? "Update to where I am" : "Use my current location"}
        </Button>
      )}
    </section>
  );
}
